import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { classNames } from '../utils';

export interface Breadcrumb {
  id: string;
  name: React.ReactNode;
  href: string;
  onClick?: () => void;
  current?: boolean;
}

interface Props {
  breadcrumbs?: Breadcrumb[];
  className?: string;
  mode?: 'default' | 'fit';
}

export default function Breadcrumbs({ breadcrumbs, className, mode }: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const finalBreadcrumbs = useMemo(() => {
    if (breadcrumbs) return breadcrumbs;

    return location.pathname
      .split('?')[0]
      .split('/')
      .map((part, index, all) => {
        if (!part) return null;
        return {
          name: part,
          href: all.slice(0, index + 1).join('/'),
          current: index + 1 === all.length,
        };
      })
      .filter((x) => !!x) as Breadcrumb[];
  }, [breadcrumbs, location]);

  return (
    <nav className={classNames(className, 'flex')} aria-label="Breadcrumb">
      <ol
        role="list"
        className={classNames(
          'flex items-center space-x-2',
          mode === 'fit' && 'w-full',
        )}
      >
        {finalBreadcrumbs.map((breadcrumb, i) => (
          <li
            key={breadcrumb.id}
            className={classNames(
              'flex items-center',
              mode === 'fit' && 'truncate',
              mode === 'fit' && i + 1 === finalBreadcrumbs.length
                ? 'shrink-0'
                : '',
            )}
          >
            {i > 0 && (
              <svg
                className="h-5 w-5 shrink-0 text-gray-300"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
              </svg>
            )}
            <Tooltip
              open={i + 1 === finalBreadcrumbs.length ? false : undefined}
            >
              <TooltipTrigger
                asChild
                className={classNames(
                  mode === 'fit' && 'flex-1 shrink truncate',
                )}
              >
                <span
                  onClick={() => {
                    navigate(breadcrumb.href);
                    breadcrumb.onClick?.();
                  }}
                  className={classNames(
                    'cursor-pointer truncate text-sm font-medium text-gray-500 hover:text-gray-700',
                    i > 0 ? 'ml-2' : '',
                  )}
                  aria-current={breadcrumb.current ? 'page' : undefined}
                >
                  {breadcrumb.name}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{breadcrumb.name}</p>
              </TooltipContent>
            </Tooltip>
          </li>
        ))}
      </ol>
    </nav>
  );
}
